import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { useCopilotReadable } from "@copilotkit/react-core";
import { alertEmitter } from "./alerts";
import { AlertBar } from "./AlertBar";
import { BriefingsModal } from "./BriefingsModal";
import {
  ALERT_AUTO_DISMISS_MS,
  ALERT_MUTE_MS,
  STORAGE_KEYS,
} from "./constants";
import { useDashboardController } from "./DashboardController";
import { KeyboardHelp } from "./KeyboardHelp";
import { Pane, type PaneHandle, type PaneKeyboardCommand } from "./Pane";
import { useNotesUI, type NoteTarget } from "@/notes/NotesUIController";
import { DEFAULT_SEVERITY_THRESHOLDS, normalizeThresholds } from "./severity";
import { SettingsModal } from "./SettingsModal";
import { booleanValue, numberInRange, useStoredState } from "./storage";
import { Splitter } from "./Splitter";
import { StatusBar } from "./StatusBar";
import { TradingToolbar } from "./TradingToolbar";
import type { PaneSide, TradingAlert } from "./types";
import { useNow } from "./useNow";
import { useTradingFeed } from "./useTradingFeed";
import { useTradingKeyboard } from "./useTradingKeyboard";
import { TrialBanner } from "@/billing/TrialBanner";
import { PastDueBanner } from "@/billing/PastDueBanner";
import { RecentNotesPanel } from "@/notes/RecentNotesPanel";
import { NotesSlideOver } from "@/notes/NotesSlideOver";
import { useCurrentUser } from "@/shell/useCurrentUser";

type ActiveModal = "settings" | "briefings" | null;
type PaneKeyboardCommandInput =
  | { type: "move"; direction: 1 | -1 }
  | { type: "toggle" }
  | { type: "escape" }
  | { type: "refresh" };

function finiteNumber(fallback: number) {
  return (value: unknown): number => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
  };
}

function paneRefFor(
  pane: PaneSide,
  leftRef: RefObject<PaneHandle>,
  rightRef: RefObject<PaneHandle>,
) {
  return pane === "left" ? leftRef : rightRef;
}

function playAlertTick() {
  const AudioContextConstructor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) return;
  const context = new AudioContextConstructor();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = 880;
  gain.gain.value = 0.05;
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.12);
  window.setTimeout(() => void context.close(), 180);
}

function notifyAlert(alert: TradingAlert, userActivated: boolean) {
  if (!document.hidden || !("Notification" in window) || !userActivated) return;
  if (Notification.permission === "granted") {
    new Notification(alert.title, { body: alert.materialId });
  } else if (Notification.permission === "default") {
    void Notification.requestPermission().then((permission) => {
      if (permission === "granted") new Notification(alert.title, { body: alert.materialId });
    });
  }
}

export function TradingView() {
  const now = useNow();
  const { data: me } = useCurrentUser();
  const currentUserId = me?.user_id ?? null;
  // Slice 3 / Task F1: dashboard pane state lives in
  // DashboardControllerProvider (mounted in AppShell) so CopilotKit
  // actions like `switch_material` can drive the panes from any route.
  // The controller wraps useStoredState for left/right material ids, so
  // cross-session persistence is preserved.
  const {
    leftMaterialId,
    rightMaterialId,
    setLeftMaterialId,
    setRightMaterialId,
    focusedSide: focusedPane,
    setFocusedSide: setFocusedPane,
    selectedClusterId,
    setSelectedClusterId,
    expandedAnalysisId,
    setExpandedAnalysisId,
  } = useDashboardController();
  const [splitRatio, setSplitRatio] = useStoredState(
    STORAGE_KEYS.splitRatio,
    0.5,
    numberInRange(0.5, 0.25, 0.75),
  );
  const [soundEnabled, setSoundEnabled] = useStoredState(
    STORAGE_KEYS.soundEnabled,
    true,
    booleanValue(true),
  );
  const [severityThresholds, setSeverityThresholds] = useStoredState(
    STORAGE_KEYS.severityThresholds,
    DEFAULT_SEVERITY_THRESHOLDS,
    normalizeThresholds,
  );
  const [mutedUntilTs, setMutedUntilTs] = useStoredState(
    STORAGE_KEYS.mutedUntilTs,
    0,
    finiteNumber(0),
  );
  const [fullscreenPane, setFullscreenPane] = useState<PaneSide | null>(null);
  const [leftSearchFocusSignal, setLeftSearchFocusSignal] = useState(0);
  const [rightSearchFocusSignal, setRightSearchFocusSignal] = useState(0);
  const [keyboardCommand, setKeyboardCommand] = useState<PaneKeyboardCommand | null>(null);
  const [alertQueue, setAlertQueue] = useState<TradingAlert[]>([]);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
  // Notes UI state lives in NotesUIControllerProvider (mounted at AppShell)
  // so the AskTapelens chat's `open_notes` action can drive the same UI.
  const {
    notesOpen,
    openedTarget,
    openNotes: openNotesController,
    setNotesOpen,
    closeNotes,
  } = useNotesUI();
  const leftPaneRef = useRef<PaneHandle>(null);
  const rightPaneRef = useRef<PaneHandle>(null);
  const userActivatedRef = useRef(false);
  const keyboardSequenceRef = useRef(0);

  const leftQuery = useTradingFeed(leftMaterialId);
  const rightQuery = useTradingFeed(rightMaterialId);

  // L5 quality fix: FocusedMaterialContext was deleted post-Slice 2.
  // The dashboard_focus readable below now carries focusedSide +
  // leftMaterial + rightMaterial, so a separate per-material publication
  // path is redundant. The dead-write useEffect that lived here was
  // removed along with the context file and AppShell provider mount.

  // Slice 2 / Task F4 + Slice 3 / Task F1: publish a `dashboard_focus`
  // readable for the per-turn composer. selectedClusterId +
  // expandedAnalysisId come from DashboardController (Task F1 lifted the
  // dashboard state to AppShell) — closing out the slice-2 TODO and spec
  // §9.1 amendment A6. Null still means "no selection".
  //
  // L4 quality fix: severityThresholds is intentionally NOT included.
  // The backend per-turn composer doesn't read it, so shipping it wasted
  // ~30-80 tokens per turn for no agent-visible benefit. If a future
  // composer pass needs it, re-add at that point.
  useCopilotReadable({
    description: "dashboard_focus",
    value: {
      focusedSide: focusedPane,
      leftMaterial: leftMaterialId,
      rightMaterial: rightMaterialId,
      selectedClusterId,
      expandedAnalysisId,
    },
  });

  const enqueueAlert = useCallback((alert: TradingAlert) => {
    setAlertQueue((queue) => {
      return [alert, ...queue.filter((queuedAlert) => queuedAlert.id !== alert.id)].slice(0, 5);
    });
  }, []);

  useEffect(() => {
    const markActivated = () => {
      userActivatedRef.current = true;
    };
    window.addEventListener("keydown", markActivated, { once: true });
    window.addEventListener("pointerdown", markActivated, { once: true });
    return () => {
      window.removeEventListener("keydown", markActivated);
      window.removeEventListener("pointerdown", markActivated);
    };
  }, []);

  useEffect(() => {
    const listener = (event: Event) => {
      const alert = (event as CustomEvent<TradingAlert>).detail;
      if (mutedUntilTs > Date.now()) return;
      enqueueAlert(alert);
      if (soundEnabled) playAlertTick();
      notifyAlert(alert, userActivatedRef.current);
    };
    alertEmitter.addEventListener("trading-alert", listener);
    return () => alertEmitter.removeEventListener("trading-alert", listener);
  }, [enqueueAlert, mutedUntilTs, soundEnabled]);

  useEffect(() => {
    if (alertQueue.length === 0) return;
    const alertId = alertQueue[0].id;
    const timeoutId = window.setTimeout(() => {
      setAlertQueue((queue) => queue.filter((alert) => alert.id !== alertId));
    }, ALERT_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timeoutId);
  }, [alertQueue]);

  const swapPanes = useCallback(() => {
    setLeftMaterialId(rightMaterialId);
    setRightMaterialId(leftMaterialId);
  }, [leftMaterialId, rightMaterialId, setLeftMaterialId, setRightMaterialId]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(!soundEnabled);
  }, [setSoundEnabled, soundEnabled]);

  const muteAlerts = useCallback(() => {
    setMutedUntilTs(Date.now() + ALERT_MUTE_MS);
    setAlertQueue([]);
  }, [setMutedUntilTs]);

  const focusPane = useCallback((side: PaneSide) => {
    setFocusedPane(side);
    setFullscreenPane((current) => (current ? side : current));
  }, []);

  const focusPaneAndElement = useCallback((side: PaneSide) => {
    focusPane(side);
    paneRefFor(side, leftPaneRef, rightPaneRef).current?.focusPane();
  }, [focusPane]);

  const issuePaneCommand = useCallback((command: PaneKeyboardCommandInput) => {
    setKeyboardCommand({
      ...command,
      pane: focusedPane,
      sequence: (keyboardSequenceRef.current += 1),
    } as PaneKeyboardCommand);
  }, [focusedPane]);

  const focusPickerSearch = useCallback((side: PaneSide) => {
    focusPaneAndElement(side);
    if (side === "left") setLeftSearchFocusSignal((value) => value + 1);
    else setRightSearchFocusSignal((value) => value + 1);
  }, [focusPaneAndElement]);

  const jumpToAlert = useCallback((alert: TradingAlert) => {
    setFocusedPane(alert.pane);
    setFullscreenPane((current) => (current ? alert.pane : current));
    const paneRef = paneRefFor(alert.pane, leftPaneRef, rightPaneRef);
    paneRef.current?.focusPane();
    paneRef.current?.scrollToItem(alert.targetId);
    setAlertQueue((queue) => queue.filter((queuedAlert) => queuedAlert.id !== alert.id));
  }, []);

  const dismissAlert = useCallback((alertId: string) => {
    setAlertQueue((queue) => queue.filter((alert) => alert.id !== alertId));
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) void document.exitFullscreen();
      return;
    }
    if (document.documentElement.requestFullscreen) {
      void document.documentElement.requestFullscreen();
    }
  }, []);

  const togglePaneFullscreen = useCallback((side: PaneSide) => {
    setFocusedPane(side);
    setFullscreenPane((current) => (current === side ? null : side));
  }, []);

  const toggleFocusedPaneFullscreen = useCallback(() => {
    togglePaneFullscreen(focusedPane);
  }, [focusedPane, togglePaneFullscreen]);

  const ignoreFeedStatusChange = useCallback(() => undefined, []);

  const openNotes = useCallback(
    (target: NoteTarget) => {
      openNotesController(target);
    },
    [openNotesController],
  );

  useTradingKeyboard({
    focusedPane,
    onEscape: () => {
      if (keyboardHelpOpen) {
        setKeyboardHelpOpen(false);
        return;
      }
      issuePaneCommand({ type: "escape" });
    },
    onFocusPane: focusPaneAndElement,
    onFocusPickerSearch: focusPickerSearch,
    onMoveSelection: (direction) => issuePaneCommand({ type: "move", direction }),
    onMute: muteAlerts,
    onRefresh: () => issuePaneCommand({ type: "refresh" }),
    onShowHelp: () => setKeyboardHelpOpen(true),
    onSwap: swapPanes,
    onToggleFullscreen: toggleFocusedPaneFullscreen,
    onToggleSelected: () => issuePaneCommand({ type: "toggle" }),
  });

  const jumpToOtherPane = useCallback(
    (sourceSide: PaneSide, clusterId: string, materialId: string) => {
      const targetSide: PaneSide = sourceSide === "left" ? "right" : "left";
      if (targetSide === "left") setLeftMaterialId(materialId);
      else setRightMaterialId(materialId);
      setFocusedPane(targetSide);
      setFullscreenPane((current) => (current ? targetSide : current));
      window.setTimeout(() => {
        document
          .querySelector<HTMLElement>(`[data-pane-side="${targetSide}"] [data-cluster-id="${clusterId}"]`)
          ?.scrollIntoView({ block: "center" });
      }, 0);
    },
    [setLeftMaterialId, setRightMaterialId],
  );

  const jumpToBriefingCluster = useCallback((clusterId: string) => {
    const leftHasCluster = leftQuery.data?.clusters.some((cluster) => cluster.cluster_id === clusterId);
    const rightHasCluster = rightQuery.data?.clusters.some((cluster) => cluster.cluster_id === clusterId);
    const targetPane: PaneSide | null = leftHasCluster ? "left" : rightHasCluster ? "right" : null;
    if (!targetPane) return false;
    focusPane(targetPane);
    paneRefFor(targetPane, leftPaneRef, rightPaneRef).current?.scrollToItem(clusterId);
    return true;
  }, [focusPane, leftQuery.data?.clusters, rightQuery.data?.clusters]);

  return (
    <div className="trading-shell">
      <PastDueBanner />
      <TrialBanner />
      <TradingToolbar
        mutedUntilTs={mutedUntilTs}
        now={now}
        onOpenBriefings={() => setActiveModal("briefings")}
        onOpenKeyboardHelp={() => setKeyboardHelpOpen(true)}
        onOpenNotes={() => {
          if (notesOpen) {
            closeNotes();
          } else {
            openNotesController(null);
          }
        }}
        onOpenSettings={() => setActiveModal("settings")}
        onSwapPanes={swapPanes}
        onToggleFullscreen={toggleFullscreen}
        onToggleSound={toggleSound}
        soundEnabled={soundEnabled}
      />
      <AlertBar
        alert={mutedUntilTs > now ? null : alertQueue[0] ?? null}
        now={now}
        onDismiss={dismissAlert}
        onJump={jumpToAlert}
      />
      <Splitter
        focusedPane={focusedPane}
        fullscreenPane={fullscreenPane}
        left={
          <Pane
            focused={focusedPane === "left"}
            fullscreen={fullscreenPane === "left"}
            keyboardCommand={keyboardCommand}
            materialId={leftMaterialId}
            mutedUntilTs={mutedUntilTs}
            onAnalysisExpand={setExpandedAnalysisId}
            onClusterSelect={setSelectedClusterId}
            onFeedStatusChange={ignoreFeedStatusChange}
            onFocusPane={focusPane}
            onJumpToOtherPane={(clusterId, materialId) => jumpToOtherPane("left", clusterId, materialId)}
            onMaterialChange={setLeftMaterialId}
            onOpenNotes={openNotes}
            onToggleFullscreen={togglePaneFullscreen}
            otherMaterialId={rightMaterialId}
            ref={leftPaneRef}
            searchFocusSignal={leftSearchFocusSignal}
            severityThresholds={severityThresholds}
            side="left"
          />
        }
        onRatioChange={setSplitRatio}
        ratio={splitRatio}
        right={
          <Pane
            focused={focusedPane === "right"}
            fullscreen={fullscreenPane === "right"}
            keyboardCommand={keyboardCommand}
            materialId={rightMaterialId}
            mutedUntilTs={mutedUntilTs}
            onAnalysisExpand={setExpandedAnalysisId}
            onClusterSelect={setSelectedClusterId}
            onFeedStatusChange={ignoreFeedStatusChange}
            onFocusPane={focusPane}
            onJumpToOtherPane={(clusterId, materialId) => jumpToOtherPane("right", clusterId, materialId)}
            onMaterialChange={setRightMaterialId}
            onOpenNotes={openNotes}
            onToggleFullscreen={togglePaneFullscreen}
            otherMaterialId={leftMaterialId}
            ref={rightPaneRef}
            searchFocusSignal={rightSearchFocusSignal}
            severityThresholds={severityThresholds}
            side="right"
          />
        }
      />
      <KeyboardHelp open={keyboardHelpOpen} onClose={() => setKeyboardHelpOpen(false)} />
      <BriefingsModal
        onClose={() => setActiveModal(null)}
        onClusterJump={jumpToBriefingCluster}
        open={activeModal === "briefings"}
      />
      <SettingsModal
        onChangeThresholds={setSeverityThresholds}
        onClose={() => setActiveModal(null)}
        open={activeModal === "settings"}
        thresholds={severityThresholds}
      />
      <StatusBar leftQuery={leftQuery} rightQuery={rightQuery} />
      {/* Notes panels — RecentNotesPanel shows when notesOpen + no specific target;
          NotesSlideOver shows when a specific target is selected */}
      {notesOpen && openedTarget === null ? (
        <RecentNotesPanel
          open={notesOpen}
          onOpenChange={(v) => setNotesOpen(v)}
        />
      ) : null}
      {notesOpen && openedTarget !== null ? (
        <NotesSlideOver
          open={notesOpen}
          onOpenChange={(v) => setNotesOpen(v)}
          target={{
            ...openedTarget,
            title: openedTarget.title ?? null,
          }}
          currentUserId={currentUserId}
        />
      ) : null}
    </div>
  );
}
